import { useState, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { useToast } from '@/hooks/errors/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { PrivateImageGeneratorClient } from '@/hooks/private-image-generator/api/client';

interface UseAddProjectImageToProjectOptions {
  onSuccess?: (projectId: string | null) => void;
  onError?: (error: Error) => void;
}

/**
 * Specialized hook for managing project assignment for Project Image Library images
 * These images already have workbook_id in their data, so no need to query current state
 */
export const useAddProjectImageToProject = (
  imageId: string, 
  currentProjectId: string | null,
  options: UseAddProjectImageToProjectOptions = {}
) => {
  const { toast } = useToast();
  const { user } = useUser();
  const queryClient = useQueryClient();
  
  // State for optimistic updates only
  const [optimisticProjectId, setOptimisticProjectId] = useState<string | null>(null);
  const [hasOptimisticUpdate, setHasOptimisticUpdate] = useState(false);

  // Get effective project ID (optimistic or passed in)
  const effectiveCurrentProjectId = hasOptimisticUpdate ? optimisticProjectId : currentProjectId;

  // Query to check image ownership
  const { data: imageOwnership } = useQuery({
    queryKey: ['image-ownership', imageId],
    queryFn: async () => {
      if (!imageId) return null;
      
      const { data, error } = await supabase
        .from('workbook_images')
        .select('user_id')
        .eq('id', imageId)
        .single();

      if (error) {
        console.error('[useAddProjectImageToProject] Error checking image ownership:', error);
        throw error;
      }
      
      return data;
    },
    enabled: !!imageId,
    staleTime: 1000 * 60 * 5, // 5 minutes - ownership doesn't change often
  });

  // Determine if current user owns this image
  const isOwner = imageOwnership?.user_id === user?.id;

  // Mutation for updating image project assignment
  const updateMutation = useMutation({
    mutationFn: async (projectId: string | null) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      // Check ownership before allowing update
      if (!isOwner) {
        throw new Error('You do not have permission to modify this image');
      }
      
      return await PrivateImageGeneratorClient.updateImageProject(imageId, user.id, projectId);
    },
    onMutate: async (projectId: string | null) => {
      // Optimistic update
      setOptimisticProjectId(projectId);
      setHasOptimisticUpdate(true);
    },
    onSuccess: (data, projectId) => {
      // Clear optimistic update
      setHasOptimisticUpdate(false);
      
      // Comprehensive cache invalidation for project image assignments
      // Invalidate private image generator caches
      queryClient.invalidateQueries({ queryKey: ['private-image-generator'] });
      queryClient.invalidateQueries({ queryKey: ['private-image-generator-count'] });
      
      // FIXED: Invalidate workbook/project data caches with exact: false for filter variations
      queryClient.invalidateQueries({ queryKey: ['workbook-projects'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['workbook-images'] });
      
      // CRITICAL: Invalidate ALL project image library caches
      queryClient.invalidateQueries({ queryKey: ['private-image-library'] }); // All projects
      queryClient.invalidateQueries({ queryKey: ['private-image-library-count'] }); // All counts
      
      // Also invalidate the specific project caches
      if (currentProjectId) {
        queryClient.invalidateQueries({ queryKey: ['private-image-library', currentProjectId] });
        queryClient.invalidateQueries({ queryKey: ['private-image-library-count', currentProjectId] });
      }
      if (projectId && projectId !== currentProjectId) {
        queryClient.invalidateQueries({ queryKey: ['private-image-library', projectId] });
        queryClient.invalidateQueries({ queryKey: ['private-image-library-count', projectId] });
      }
      
      // Invalidate universal storage caches
      queryClient.invalidateQueries({ queryKey: ['universal-storage-items'] });
      queryClient.invalidateQueries({ queryKey: ['universal-library-data'] });
      
      // CRITICAL: Invalidate the infinite user images sidebar cache
      queryClient.invalidateQueries({ queryKey: ['infinite-user-images'] });
      
      // Success feedback
      toast({
        title: "Success",
        description: projectId ? "Image added to project" : "Image removed from project",
        variant: "default",
      });
      
      options.onSuccess?.(projectId);
    },
    onError: (error) => {
      // Clear optimistic update on error
      setHasOptimisticUpdate(false);
      
      console.error('Failed to update image project:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update project assignment",
        variant: "destructive",
      });
      
      options.onError?.(error);
    }
  });

  // Remove from project mutation
  const removeMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      // Check ownership before allowing removal
      if (!isOwner) {
        throw new Error('You do not have permission to modify this image');
      }
      
      return await PrivateImageGeneratorClient.updateImageProject(imageId, user.id, null);
    },
    onMutate: async () => {
      // Optimistic update to null
      setOptimisticProjectId(null);
      setHasOptimisticUpdate(true);
    },
    onSuccess: () => {
      // Clear optimistic update
      setHasOptimisticUpdate(false);
      
      // Same cache invalidation as update
      queryClient.invalidateQueries({ queryKey: ['private-image-generator'] });
      queryClient.invalidateQueries({ queryKey: ['private-image-generator-count'] });
      queryClient.invalidateQueries({ queryKey: ['workbook-projects'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['workbook-images'] });
      queryClient.invalidateQueries({ queryKey: ['private-image-library'] });
      queryClient.invalidateQueries({ queryKey: ['private-image-library-count'] });
      
      if (currentProjectId) {
        queryClient.invalidateQueries({ queryKey: ['private-image-library', currentProjectId] });
        queryClient.invalidateQueries({ queryKey: ['private-image-library-count', currentProjectId] });
      }
      
      queryClient.invalidateQueries({ queryKey: ['universal-storage-items'] });
      queryClient.invalidateQueries({ queryKey: ['universal-library-data'] });
      
      // CRITICAL: Invalidate the infinite user images sidebar cache
      queryClient.invalidateQueries({ queryKey: ['infinite-user-images'] });
      
      toast({
        title: "Success",
        description: "Image removed from project",
        variant: "default",
      });
      
      options.onSuccess?.(null);
    },
    onError: (error) => {
      // Clear optimistic update on error
      setHasOptimisticUpdate(false);
      
      console.error('Failed to remove image from project:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to remove from project",
        variant: "destructive",
      });
      
      options.onError?.(error);
    }
  })

  // Public methods
  const updateImageProject = useCallback(async (projectId: string | null) => {
    await updateMutation.mutateAsync(projectId);
  }, [updateMutation]);

  const removeFromProject = useCallback(async () => {
    await removeMutation.mutateAsync();
  }, [removeMutation]);

  const isPending = updateMutation.isPending || removeMutation.isPending;

  return {
    currentProjectId: effectiveCurrentProjectId,
    updateImageProject,
    removeFromProject,
    isPending,
    isOwner, // Now properly checked against actual image ownership
  };
}; 